import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
/* One shared stylesheet covers all service pages */
import "./styles/Services.css";
import heroBg from "../../assets/images/services/BusinessStrategy/BusinessStrategyHero.png";

/* ── Static data outside component (no re-allocation on render) ── */
const serviceItems = [
  { label: "01", title: "Assess",  desc: "Understand systems, processes, risks, and what's blocking progress." },
  { label: "02", title: "Align",   desc: "Connect business priorities to technology initiatives and funding." },
  { label: "03", title: "Execute", desc: "Create a phased roadmap with governance and measurable KPIs." },
];

const kpiCards = [
  {
    title: "Operational efficiency",
    text:  "Cycle time, ticket volume, automation coverage, and service reliability.",
  },
  {
    title: "Risk reduction",
    text:  "Control coverage, audit readiness, incident trends, and security posture improvements.",
  },
  {
    title: "Business value",
    text:  "Adoption, customer experience metrics, cost optimisation, and measurable ROI.",
  },
];

const benefitBlocks = [
  {
    title: "Clear priorities",
    text:  "Stop competing initiatives and focus teams on the work that matters most.",
  },
  {
    title: "Better decisions",
    text:  "Improve intake, funding, and decision rights with a consistent governance model.",
  },
  {
    title: "Measurable outcomes",
    text:  "Track KPIs that show progress and value — then iterate the roadmap as needs change.",
  },
];

/* ── Component ── */
const BusinessStrategy = () => {
  const navigate = useNavigate();

  /* Tabs */
  const tabs = useMemo(
    () => [
      {
        key: "alignment",
        label: "Business Alignment",
        title: "Business-Aligned IT Strategy",
        desc: "Align technology priorities with business goals so IT spend supports outcomes that matter — growth, efficiency, customer experience, and risk reduction.",
        cta: "Learn More",
        image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1400&q=80",
      },
      {
        key: "roadmap",
        label: "Roadmap & Priorities",
        title: "Roadmap, Prioritisation & Execution Plan",
        desc: "Turn strategy into an actionable roadmap with milestones, dependencies, and measurable KPIs — so teams can deliver in phases without creating chaos.",
        cta: "Learn More",
        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
      },
      {
        key: "governance",
        label: "Governance & Metrics",
        title: "Governance, Decision Rights & Reporting",
        desc: "Establish clear decision making, intake, and reporting so initiatives stay aligned, funded correctly, and measured against real business value.",
        cta: "Learn More",
        image: "https://images.unsplash.com/photo-1523952578875-e6bb18b26645?auto=format&fit=crop&w=1400&q=80",
      },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(tabs[0]);

  /* Card scroll-reveal */
  const cardRefs = useRef([]);
  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* CTA reveal */
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setCtaInView(true); obs.unobserve(el); }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* Benefits reveal */
  const benefitsRef = useRef(null);
  const [benefitsInView, setBenefitsInView] = useState(false);
  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBenefitsInView(true); },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="servicePage">

      {/* ── HERO ── */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">Business Strategy</div>

          <h1 className="hc-hero__title">
            Transform your business<br />
            with strategic IT consulting.
          </h1>

          <p className="hc-hero__lead">
            Align technology investments with business goals to drive smart digital
            transformation, reduce waste, and deliver measurable outcomes.
          </p>

          <button
            className="btn-orange"
            type="button"
            onClick={() => navigate("/contact")}
          >
            Meet Our Experts
            <svg className="btn-arrow" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <img className="hero-img" src={heroBg} alt="Business strategy consulting" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── SERVICES INTRO ── */}
      <section className="services-section">
        <div className="services-left">
          <p className="section-eyebrow">
            <span className="live-dot" aria-hidden="true" />
            Our approach
          </p>
          <h2>Clarity first. Execution next.</h2>
          <p>
            Strategy fails when it stays in slides. This service turns business goals
            into a practical IT plan with priorities, governance, and tracking — so
            teams ship the right work and can prove impact.
          </p>

          <ul className="bs-bullets">
            <li>Current-state assessment and gap identification.</li>
            <li>Future-state capabilities and target outcomes.</li>
            <li>Roadmap with phases, owners, and success metrics.</li>
          </ul>
        </div>

        <div className="services-right">
          {serviceItems.map(({ label, title, desc }, i) => (
            <div
              key={title}
              ref={(el) => (cardRefs.current[i] = el)}
              className="service-item"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="service-icon-IT" aria-hidden="true">{label}</div>
              <div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MANAGED TABS (what we deliver) ── */}
      <section className="managed-IT-section">
        <div className="managed-IT-header">
          <p className="section-eyebrow" style={{ color: "rgba(255,255,255,0.45)", justifyContent: "center" }}>
            <span className="live-dot" aria-hidden="true" />
            Deliverables
          </p>
          <h2>What we deliver</h2>
          <p>
            Engage for alignment, roadmapping, or governance — or combine them
            into a full transformation plan.
          </p>

          <div className="managed-tabs" role="tablist" aria-label="Business strategy deliverables">
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab.key === t.key}
                className={`managed-tab${activeTab.key === t.key ? " active" : ""}`}
                type="button"
                onClick={() => setActiveTab(t)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="managed-IT-body" key={activeTab.key}>
          <div className="managed-card">
            <div className="managed-imageWrap">
              <img
                className="managed-image"
                src={activeTab.image}
                alt={activeTab.title}
              />
            </div>

            <div className="managed-content">
              <h3>{activeTab.title}</h3>
              <p>{activeTab.desc}</p>
              <button
                className="managed-cta"
                type="button"
                onClick={() => navigate("/contact")}
              >
                {activeTab.cta} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── KPI METRICS (dark ink section) ── */}
      <section className="bs-kpi">
        <div className="bs-kpi__container">
          <p className="section-eyebrow" style={{
            color: "rgba(255,255,255,0.45)",
            justifyContent: "center",
            marginBottom: "0.75rem",
          }}>
            <span className="live-dot" aria-hidden="true" />
            Measurement
          </p>
          <h2 className="bs-kpi__title">Track outcomes, not activity</h2>
          <p className="bs-kpi__lead">
            Roadmaps work best when progress is measured with clear KPIs tied
            to business outcomes — not just tasks completed.
          </p>

          <div className="bs-kpi__grid">
            {kpiCards.map((c) => (
              <div key={c.title} className="bs-kpiCard">
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="IT-cta-section">
        <div
          ref={ctaRef}
          className={`IT-cta-card IT-cta-reveal${ctaInView ? " in" : ""}`}
        >
          <div className="IT-cta-left">
            <img
              className="IT-cta-illustration"
              src="https://images.unsplash.com/photo-1556761175-129418cb2dfe?auto=format&fit=crop&w=1400&q=80"
              alt="Strategy workshop"
            />
          </div>

          <div className="IT-cta-right">
            <h2>
              Build a practical roadmap for{" "}
              <span>IT transformation</span>
            </h2>
            <p>
              Get a clear strategy, prioritised initiatives, and governance that keeps
              execution aligned to business goals. Start with a discovery call to
              define scope and outcomes.
            </p>
            <button
              className="IT-cta-btn"
              type="button"
              onClick={() => navigate("/contact")}
            >
              Find My Strategy Plan →
            </button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="IT-benefits-section">
        <div className="IT-benefits-wrap" ref={benefitsRef}>
          <div className={`benefits-left${benefitsInView ? " in" : ""}`}>
            <p className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Why it works
            </p>
            <h2>Business strategy that drives delivery</h2>
            <div className="benefits-underline" />
            <p>
              Strong IT strategy uses governance and measurement to keep execution
              aligned to priorities — so investments deliver value instead of noise.
            </p>
          </div>

          <div className={`benefits-right${benefitsInView ? " in" : ""}`}>
            {benefitBlocks.map((b, i) => (
              <div
                key={b.title}
                className="benefit-block"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default BusinessStrategy;